import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useUser from "../../hooks/useUser";
import { getVgsapReport, getVgsapCpgData, getVgsapReportPromotions, downloadVgsapBulletinPDF } from "../../service/SalesNewsVgsap";
import { PageLoader } from "../../component/loader/PLoader";
import ErrorHandler from "../../component/errors/ErrorHandler";
import {
	Box, Typography, Paper, Breadcrumbs, Button,
	TextField, Autocomplete, Snackbar, Alert, Checkbox,
	Accordion, AccordionSummary, AccordionDetails, Chip, Divider, Tabs, Tab
} from "@mui/material";
import { DataGrid, GridToolbarContainer, GridToolbarDensitySelector } from "@mui/x-data-grid";
import dayjs from "dayjs";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { createExcel } from "../../utils/Utils";
import { EditableDatePicker } from "../../component/datePicker";
import useShiftRangeSelect from "../../component/searchPage/useShiftRangeSelect";
import "./salesNewsVgsap.css";
import { formatDate, POINT_SECTIONS } from "./vgsapUtils";

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Default fiscal year start (April 1 of previous fiscal year)
function getDefaultStartDate() {
	const now = new Date();
	const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
	return `${year}-04-01`;
}

function getDefaultEndDate() {
	return new Date().toISOString().split("T")[0];
}

// Min date: 10 years ago (to prevent accidental mega-queries)
function getMinDate() {
	const now = new Date();
	return `${now.getFullYear() - 10}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// Max date: today (reports show historical data)
function getMaxDate() {
	return new Date().toISOString().split("T")[0];
}

function BulletinReport() {
	const { t } = useTranslation();
	const { isSalesNewsAdmin } = useUser();

	// Form state
	const [selectedCPG, setSelectedCPG] = useState([{ key: "All", value: "All" }]);
	const [startDate, setStartDate] = useState(getDefaultStartDate());
	const [endDate, setEndDate] = useState(getDefaultEndDate());
	const [startDateError, setStartDateError] = useState("");
	const [endDateError, setEndDateError] = useState("");
	const [shouldFetch, setShouldFetch] = useState(false);
	const [fetchParams, setFetchParams] = useState(null);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);
	const [selectedRows, setSelectedRows] = useState([]);
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'warning' });
	const [resultTabIndex, setResultTabIndex] = useState(0);

	// Fetch CPG list for dropdown
	const { data: cpgList, isLoading: cpgLoading } = useQuery({
		queryKey: ["vgsapCpgData"],
		queryFn: getVgsapCpgData,
	});

	// Fetch report data only when user clicks "Generate Report"
	const { data: reportData, isLoading: reportLoading, error: reportError, isFetching: reportFetching } = useQuery({
		queryKey: ["vgsapReport", fetchParams?.scdCode, fetchParams?.startDate, fetchParams?.endDate, page, pageSize],
		queryFn: () => getVgsapReport(fetchParams?.scdCode, fetchParams?.startDate, fetchParams?.endDate, page, pageSize),
		enabled: shouldFetch && !!fetchParams,
	});

	// Fetch promotion summary points for the same criteria (DisplayReport behavior)
	const { data: promoSummaryData, isLoading: promoSummaryLoading } = useQuery({
		queryKey: ["vgsapReportPromotions", fetchParams?.scdCode, fetchParams?.startDate, fetchParams?.endDate],
		queryFn: () => getVgsapReportPromotions(fetchParams?.scdCode, fetchParams?.startDate, fetchParams?.endDate),
		enabled: isSalesNewsAdmin && shouldFetch && !!fetchParams && !!reportData?.rows?.length,
	});

	// Build CPG options: "All" + list from DB
	const cpgOptions = [
		{ key: "All", value: "All" },
		...(Array.isArray(cpgList) ? cpgList : []).map(c => ({ key: c.scd_code, value: `${c.scd_code} - ${c.schedule}` })),
	];

	// Applies the CPG "All" toggle rules to a raw selection array and commits it.
	// Shared by the normal onChange path and the Shift-range path so both behave
	// identically.
	const commitCPG = (v) => {
		// Handle "All" toggle logic
		const hasAll = v.some(c => c.key === "All");
		const prevHadAll = selectedCPG.some(c => c.key === "All");

		if (v.length === 0) {
			setSelectedCPG([{ key: "All", value: "All" }]);
		} else if (hasAll && !prevHadAll) {
			// User just selected "All" — keep only "All"
			setSelectedCPG([{ key: "All", value: "All" }]);
		} else if (hasAll && prevHadAll && v.length > 1) {
			// User selected another item while "All" active — remove "All"
			setSelectedCPG(v.filter(c => c.key !== "All"));
		} else {
			setSelectedCPG(v);
		}
	};

	// Shift+Arrow / Shift+click range selection over the CPG options. "All" is a
	// sentinel and is excluded from range spans; the "All" toggle rules above
	// still run on the committed result.
	const cpgIsEqual = (a, b) => a?.key === b?.key;
	const filteredCpgOptionsRef = useRef(cpgOptions);
	const { getAutocompleteProps: getCpgShiftProps, handleOptionClick: handleCpgOptionClick } = useShiftRangeSelect({
		getOptions: () => filteredCpgOptionsRef.current,
		getValue: () => selectedCPG,
		onRangeSelect: (next) => commitCPG(next),
		isEqual: cpgIsEqual,
		isRangeable: (option) => option?.key !== "All",
	});

	const handleGenerateReport = () => {
		// Block the API call if there are any date validation errors
		if (startDateError || endDateError) {
			setSnackbar({ open: true, message: t('Please correct the date errors before generating the report'), severity: 'warning' });
			return;
		}
		// Cross-field validation: start date must not be greater than end date
		if (startDate && endDate && dayjs(startDate).isAfter(dayjs(endDate), "day")) {
			setStartDateError("Start Date should not be greater than End Date");
			return;
		}
		// Join multiple CPG selections: "All" means all, otherwise comma-separated codes
		const hasAll = selectedCPG.some(c => c.key === "All");
		const scdCode = hasAll ? "All" : selectedCPG.map(c => c.key).join(",");
		setPage(0); // Reset to first page on new search
		setFetchParams({ scdCode, startDate, endDate });
		setShouldFetch(true);
	};

	const handleExportExcel = async () => {
		if (!reportData?.rows || reportData.rows.length === 0) return;
		setSnackbar({ open: true, message: t('Processing export...'), severity: 'info' });
		
		try {
			// Fetch all data in chunks (5000 rows per page) to avoid Lambda timeout/6MB limit
			let allRows = [];
			let page = 0;
			const pageSize = 5000;
			const totalRowCount = reportData.totalRowCount || 0;
			
			while ((page * pageSize) < totalRowCount) {
				const { rows } = await getVgsapReport(
					fetchParams?.scdCode,
					fetchParams?.startDate,
					fetchParams?.endDate,
					page,
					pageSize
				);
				
				if (!rows || rows.length === 0) break;
				allRows.push(...rows);
				page++;
				
				// Update progress
				const loadedCount = allRows.length;
				setSnackbar({
					open: true,
					message: t(`Fetching data... ${loadedCount.toLocaleString()} / ${totalRowCount.toLocaleString()} records`),
					severity: 'info'
				});
			}
			
			const cols = [
				{ headerName: "SalesNews No", field: "bulletin_no", width: 15 },
				{ headerName: "SalesNews Desc", field: "bulletin_desc", width: 40 },
				{ headerName: "SalesNews Date", field: "bulletin_dt", width: 15 },
				{ headerName: "ProdGroup", field: "prodgroup", width: 15 },
				{ headerName: "Promo Start", field: "promo_start", width: 15 },
				{ headerName: "Promo End", field: "promo_end", width: 15 },
				{ headerName: "Promo Extend date", field: "promo_extd_dt", width: 15 },
				{ headerName: "Sales News Type", field: "bulletin_type", width: 18 },
				{ headerName: "Request", field: "request", width: 20 },
				{ headerName: "Effective date", field: "effective_dt", width: 15 },
				{ headerName: "CPG", field: "schedule", width: 25 },
				{ headerName: "Designation", field: "designation", width: 25 },
				{ headerName: "Company", field: "company", width: 20 },
				{ headerName: "Customer Sold To Number", field: "customersoldto_number", width: 22 },
				{ headerName: "Customer Sold To Name", field: "customerssoldto_name", width: 22 },
				{ headerName: "Region", field: "company_id", width: 10 },
			];
			createExcel(cols, allRows);
			setSnackbar({ open: true, message: t('Export complete'), severity: 'success' });
		} catch (error) {
			console.error('Export error:', error);
			setSnackbar({ open: true, message: t('Export failed: ' + error.message), severity: 'error' });
		}
	};

	const handleExportSelected = () => {
		if (selectedRows.length === 0) {
			setSnackbar({ open: true, message: t('Please select at least one record'), severity: 'warning' });
			return;
		}
		setSnackbar({ open: true, message: t('Processing export...'), severity: 'info' });
		const rows = reportData.rows.filter(row => selectedRows.includes(`${row.bulletin_no}_${reportData.rows.indexOf(row)}`));
		const cols = [
			{ headerName: "SalesNews No", field: "bulletin_no", width: 15 },
			{ headerName: "SalesNews Desc", field: "bulletin_desc", width: 40 },
			{ headerName: "SalesNews Date", field: "bulletin_dt", width: 15 },
			{ headerName: "ProdGroup", field: "prodgroup", width: 15 },
			{ headerName: "Promo Start", field: "promo_start", width: 15 },
			{ headerName: "Promo End", field: "promo_end", width: 15 },
			{ headerName: "Promo Extend date", field: "promo_extd_dt", width: 15 },
			{ headerName: "Sales News Type", field: "bulletin_type", width: 18 },
			{ headerName: "Request", field: "request", width: 20 },
			{ headerName: "Effective date", field: "effective_dt", width: 15 },
			{ headerName: "CPG", field: "schedule", width: 25 },
			{ headerName: "Designation", field: "designation", width: 25 },
			{ headerName: "Company", field: "company", width: 20 },
			{ headerName: "Customer Sold To Number", field: "customersoldto_number", width: 22 },
			{ headerName: "Customer Sold To Name", field: "customerssoldto_name", width: 22 },
			{ headerName: "Region", field: "company_id", width: 10 },
		];
		createExcel(cols, rows);
		setSnackbar({ open: true, message: t('Export complete'), severity: 'success' });
	};

	const handleDownloadPDF = async (downloadAll) => {
		if (!reportData?.rows || reportData.rows.length === 0) return;
		
		setSnackbar({ open: true, message: t('Processing PDF download...'), severity: 'info' });
		
		try {
			let bulletinNos = [];
			
			if (downloadAll) {
				// Fetch all bulletin numbers in chunks (same as Excel export)
				let page = 0;
				const pageSize = 5000;
				const totalRowCount = reportData.totalRowCount || 0;
				
				while ((page * pageSize) < totalRowCount) {
					const { rows } = await getVgsapReport(
						fetchParams?.scdCode,
						fetchParams?.startDate,
						fetchParams?.endDate,
						page,
						pageSize
					);
					
					if (!rows || rows.length === 0) break;
					bulletinNos.push(...rows.map(row => row.bulletin_no).filter(Boolean));
					page++;
					
					// Update progress
					setSnackbar({
						open: true,
						message: t(`Preparing PDFs... ${bulletinNos.length.toLocaleString()} records`),
						severity: 'info'
					});
				}
			} else {
				// Download selected rows only
				bulletinNos = reportData.rows
					.filter(row => selectedRows.includes(`${row.bulletin_no}_${reportData.rows.indexOf(row)}`))
					.map(row => row.bulletin_no)
					.filter(Boolean);
			}
			
			if (bulletinNos.length === 0) {
				setSnackbar({ open: true, message: t('Please select at least one record'), severity: 'warning' });
				return;
			}
			
			// Deduplicate
			const uniqueNos = [...new Set(bulletinNos)];
			
			const result = await downloadVgsapBulletinPDF(uniqueNos);
			if (result.url) {
				window.open(result.url, '_blank');
				// Format missing list for display
				let displayMessage = t('Download ready');
				if (result.warning) {
					displayMessage = result.warning;
				} else if (result.missing && result.missing.length > 0) {
					const missingCount = result.missing.length;
					const previewIds = result.missing.slice(0, 5).join(', ');
					const suffix = missingCount > 5 ? ` ... and ${missingCount - 5} more` : '';
					displayMessage = t('Partial download') + `: ${missingCount} ${t('bulletins without PDFs')} (${previewIds}${suffix})`;
				}
				setSnackbar({ open: true, message: displayMessage, severity: result.warning || result.missing?.length > 0 ? 'warning' : 'success' });
			} else if (result.warning) {
				setSnackbar({ open: true, message: result.warning, severity: 'warning' });
			} else {
				setSnackbar({ open: true, message: t('No PDF files found for the selected bulletin(s). PDFs may not have been uploaded yet.'), severity: 'warning' });
			}
		} catch (err) {
			console.error('PDF download error:', err);
			setSnackbar({ open: true, message: t('Failed to download PDF: ' + err.message), severity: 'error' });
		}
	};

	return (
		<Box sx={{ p: 3 }} className="vgsap__page">
			{/* Breadcrumb */}
			<Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
				<Link to="/sales-news/sales-news-vgsap" style={{ textDecoration: "none", color: "#1976d2" }}>
					{t("Sales News (Vistex GSAP)")}
				</Link>
				<Typography color="text.primary">{t("Report")}</Typography>
			</Breadcrumbs>

			{/* Report Form */}
			<Paper sx={{ p: 3, mb: 3 }}>
				<Typography variant="h5" gutterBottom>
					{t("Sales News (Vistex GSAP) Report")}
				</Typography>

				{/* Row 1: Start Date + End Date */}
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start", mt: 2 }}>
					{/* Start Date */}
					<Box sx={{ width: 200 }}>
						<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
							{t("Start Date")}
						</Typography>
						<EditableDatePicker
							value={startDate}
							minDate={getMinDate()}
							maxDate={endDate || getMaxDate()}
							onChange={(iso) => {
								setStartDate(iso || "");
								if (iso && endDate && dayjs(iso).isAfter(dayjs(endDate), "day")) {
									setStartDateError("Start date cannot be after end date");
								} else {
									setStartDateError("");
									setEndDateError("");
								}
							}}
							error={!!startDateError}
							helperText={startDateError}
							fullWidth
						/>
					</Box>

					{/* End Date */}
					<Box sx={{ width: 200 }}>
						<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
							{t("End Date")}
						</Typography>
						<EditableDatePicker
							value={endDate}
							minDate={startDate || getMinDate()}
							maxDate={getMaxDate()}
							onChange={(iso) => {
								setEndDate(iso || "");
								if (iso && startDate && dayjs(iso).isBefore(dayjs(startDate), "day")) {
									setEndDateError("End date cannot be before start date");
								} else {
									setEndDateError("");
									setStartDateError("");
								}
							}}
							error={!!endDateError}
							helperText={endDateError}
							fullWidth
						/>
					</Box>
				</Box>

				{/* Row 2: CPG / Schedule */}
				<Box sx={{ mt: 2 }}>
					<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
						{t("CPG / Schedule")}
					</Typography>
					<Autocomplete
						multiple
						disableCloseOnSelect
						{...getCpgShiftProps()}
						options={cpgOptions}
						value={selectedCPG}
						filterOptions={(opts, state) => {
							const input = state.inputValue.toLowerCase();
							const shown = input ? opts.filter(o => o.value?.toLowerCase().includes(input)) : opts;
							// Remember what's visible for Shift-range math.
							filteredCpgOptionsRef.current = shown;
							return shown;
						}}
						onChange={(e, v) => {
							commitCPG(v);
						}}
						isOptionEqualToValue={(option, value) => option?.key === value?.key}
						getOptionLabel={(option) => option?.value || ""}
						loading={cpgLoading}
						size="small"
						renderOption={(props, option, { selected }) => {
							const { onClick, ...liProps } = props;
							return (
								<li
									{...liProps}
									key={option.key}
									onClick={(event) => {
										// Shift+click extends the range from the anchor; otherwise
										// fall through to MUI's default single-toggle.
										if (handleCpgOptionClick(event, option)) return;
										onClick?.(event);
									}}
								>
									<Checkbox
										icon={checkboxIcon}
										checkedIcon={checkedIcon}
										style={{ marginRight: 8 }}
										checked={selected}
									/>
									{option?.value || ""}
								</li>
							);
						}}
						renderInput={(params) => (
							<TextField {...params} placeholder={t("Select CPG")} />
						)}
					/>
				</Box>

				{/* Generate Report Button — bottom right */}
				<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
					<Button
						variant="contained"
						startIcon={<SearchIcon />}
						onClick={handleGenerateReport}
						disabled={reportFetching}
						sx={{ height: 40 }}
					>
						{t("Generate Report")}
					</Button>
				</Box>
			</Paper>

			{/* Report Loading */}
			{reportLoading && <PageLoader show={true} />}

			{/* Report Error */}
			{reportError && (
				<ErrorHandler title={t("Something went wrong")} desc={t("Unable to load report")} />
			)}

			{/* Report Results — Tab-based layout */}
			{reportData?.rows && Array.isArray(reportData.rows) && reportData.rows.length > 0 && (
				<Paper sx={{ mb: 3 }}>
					{/* Tabs Header */}
					<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
						<Tabs value={resultTabIndex} onChange={(e, newVal) => setResultTabIndex(newVal)}>
							<Tab label={t("Report Data")} />
							{isSalesNewsAdmin && promoSummaryData && Array.isArray(promoSummaryData) && promoSummaryData.length > 0 && (
								<Tab label={t("Promotions")} data-testid="promo-tab" />
							)}
						</Tabs>
					</Box>

					{/* Tab 1: Report DataGrid */}
					{resultTabIndex === 0 && (
						<Box sx={{ p: 2, position: 'relative' }}>
							<Typography variant="h6" gutterBottom>
								{t("Results")} ({reportData.totalRowCount} {t("records")})
							</Typography>
							{reportFetching && (
								<Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
									<PageLoader show={true} />
								</Box>
							)}
							<div className="vgsap__datagrid-container" style={{ height: 600, opacity: reportFetching ? 0.5 : 1, transition: 'opacity 0.2s' }}>
							<DataGrid
								disableRowSelectionOnClick
								keepNonExistentRowsSelected
								checkboxSelection
								disableColumnMenu
								rows={reportData.rows.map((row, idx) => ({ ...row, id: `${row.bulletin_no}_${idx}` }))}
								columns={[
									{ field: "bulletin_no", headerName: t("Sales News No"), width: 140,
										renderCell: (params) => (
											<Link to={`/sales-news/sales-news-vgsap/detail/${params.value}`} style={{ color: "#1976d2", textDecoration: "none" }}>
												{params.value}
											</Link>
										)
									},
									{ field: "bulletin_desc", headerName: t("Description"), width: 280 },
									{ field: "bulletin_dt", headerName: t("Date"), width: 120, valueFormatter: (value) => formatDate(value) },
									{ field: "bulletin_type", headerName: t("Type"), width: 140 },
									{ field: "promo_start", headerName: t("Promo Start"), width: 120, valueFormatter: (value) => formatDate(value) },
									{ field: "promo_end", headerName: t("Promo End"), width: 120, valueFormatter: (value) => formatDate(value) },
									{ field: "schedule", headerName: t("Schedule"), width: 140 },
									{ field: "designation", headerName: t("Designation"), width: 160 },
									{ field: "company", headerName: t("Company"), width: 180 },
									{ field: "company_id", headerName: t("Region"), width: 90 },
								]}
								paginationMode="server"
								rowCount={reportData.totalRowCount}
								paginationModel={{ page, pageSize }}
								onPaginationModelChange={(model) => {
									setPage(model.page);
									setPageSize(model.pageSize);
								}}
								onRowSelectionModelChange={(ids) => setSelectedRows(ids)}
								rowSelectionModel={selectedRows}
								pageSizeOptions={[25, 50, 100]}
								loading={reportFetching}
								slots={{
									toolbar: () => (
										<GridToolbarContainer>
											<Button size="small" startIcon={<DownloadIcon />} onClick={handleExportSelected} disabled={selectedRows.length === 0}>
												{t("Export Selected to Excel")}
											</Button>
											<Button size="small" startIcon={<DownloadIcon />} onClick={handleExportExcel}>
												{t("Export All to Excel")}
											</Button>
											<Button size="small" startIcon={<DownloadIcon />} onClick={() => handleDownloadPDF(false)} disabled={selectedRows.length === 0}>
												{t("Download Selected PDF")}
											</Button>
											<Button size="small" startIcon={<DownloadIcon />} onClick={() => handleDownloadPDF(true)}>
												{t("Download All PDF")}
											</Button>
											<GridToolbarDensitySelector />
										</GridToolbarContainer>
									),
								}}
							/>
							</div>
						</Box>
					)}

					{/* Tab 2: Promotion Summary (Admin only) */}
					{isSalesNewsAdmin && resultTabIndex === 1 && promoSummaryData && Array.isArray(promoSummaryData) && (
						<Box sx={{ p: 2, position: 'relative', minHeight: 600 }}>
							{promoSummaryLoading && (
								<Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
									<PageLoader show={true} />
								</Box>
							)}

							{!promoSummaryLoading && promoSummaryData.length > 0 && (
								<>
									<Typography variant="h6" gutterBottom>
										{t("Promotion Summary Points")} ({promoSummaryData.length} {t("promotions")})
									</Typography>
									<Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
										{promoSummaryData.map((promo) => {
											// Group points by section
											const pointsBySection = (promo.points || []).reduce((acc, point) => {
												const section = point.promo_bullet_id || '1';
												if (!acc[section]) acc[section] = [];
												acc[section].push(point);
												return acc;
											}, {});

											return (
												<Accordion key={promo.promo_id} defaultExpanded={false} sx={{ mb: 1 }}>
													<AccordionSummary expandIcon={<ExpandMoreIcon />}>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
															<Typography sx={{ fontWeight: 600, minWidth: 80 }}>
																{t("Promo")} {promo.promo_id}
															</Typography>
															<Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
																{promo.description}
															</Typography>
															<Chip
																label={`${formatDate(promo.start_date)} - ${formatDate(promo.end_date)}`}
																size="small"
																variant="outlined"
															/>
															<Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
																{t("Sales News")}: {promo.bulletin_no}
															</Typography>
														</Box>
													</AccordionSummary>
													<AccordionDetails>
														<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
															{Object.entries(POINT_SECTIONS).map(([sectionId, label]) => {
																const sectionPoints = pointsBySection[sectionId] || [];
																return (
																	<Box key={sectionId}>
																		<Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
																			{label}
																		</Typography>
																		{sectionPoints.length > 0 ? (
																			sectionPoints.map((point, idx) => (
																				<Typography key={idx} variant="body2" sx={{ pl: 1, mb: 0.3 }}>
																					• {point.points || '—'}
																				</Typography>
																			))
																		) : (
																			<Typography variant="body2" color="text.disabled" sx={{ pl: 1 }}>
																				{t("No data")}
																			</Typography>
																		)}
																	</Box>
																);
															})}
														</Box>
													</AccordionDetails>
												</Accordion>
											);
										})}
									</Box>
								</>
							)}

							{!promoSummaryLoading && promoSummaryData.length === 0 && (
								<Typography color="text.secondary">{t("No promotions found for the selected criteria")}</Typography>
							)}
						</Box>
					)}
				</Paper>
			)}

			{/* No Results */}
			{shouldFetch && reportData && reportData.rows && reportData.rows.length === 0 && !reportLoading && (
				<Paper sx={{ p: 3 }}>
					<Typography color="text.secondary">{t("No report data found for the selected criteria")}</Typography>
				</Paper>
			)}

			{/* Snackbar for warnings */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
}

export default BulletinReport;
